import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation, matchPath } from 'react-router-dom';
import { useScrollLock } from '../../_utils/useScrollLock';
import { hapticImpact, hapticTick } from '../../_utils/haptics';
import BookingDetailsPage from './BookingDetailsPage';
import { takeHeroHandoff, EASE } from './heroHandoff';
import './BookingSheet.css';

/* The booking details as a sheet over the bookings list. The URL still
   changes to /dashboard/bookings/:id (deep links, hardware back and the
   browser back all work) but the list stays mounted underneath with its
   scroll position, dimmed and eased back while the sheet is up. Full screen
   on phones, a centred panel on wider screens. Swipe down from the top of the
   sheet to close it. */

const CLOSE_MS = 260;
const CLOSE_DRAG = 110;
const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A fixed copy of the photo that flies between the list card and the sheet's
// hero. Plain DOM: it lives for one animation and touches nothing in React.
const makeGhost = (root, src, from, radius, kind) => {
  const g = document.createElement('div');
  g.className = 'bsheet-ghost';
  g.dataset.kind = kind;
  Object.assign(g.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px`, borderRadius: radius });
  const img = document.createElement('img');
  img.src = src; img.alt = '';
  g.appendChild(img);
  root.appendChild(g);
  return g;
};
const flyGhost = (g, to, radius, ms) => g.animate(
  [{ left: g.style.left, top: g.style.top, width: g.style.width, height: g.style.height, borderRadius: g.style.borderRadius },
   { left: `${to.left}px`, top: `${to.top}px`, width: `${to.width}px`, height: `${to.height}px`, borderRadius: radius }],
  { duration: ms, easing: EASE, fill: 'forwards' }
);
// Where the sheet's hero will sit once the panel has finished rising: its
// rect now, minus the panel's current translate.
const heroRestRect = (panel) => {
  const hero = panel.querySelector('.bd-hero');
  if (!hero) return null;
  const r = hero.getBoundingClientRect();
  const m = getComputedStyle(panel).transform;
  let ty = 0;
  if (m && m !== 'none') { const parts = m.match(/matrix\(([^)]+)\)/); if (parts) ty = parseFloat(parts[1].split(',')[5]) || 0; }
  return { left: r.left, top: r.top - ty, width: r.width, height: r.height };
};
const wide = () => window.matchMedia('(min-width: 769px)').matches;

const BookingSheet = ({ id, open }) => {
  const history = useHistory();
  const scrollerRef = useRef(null);
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const rootRef = useRef(null);
  const handoffRef = useRef(null);
  // Once the rise has finished the panel drops its keyframe for a plain
  // transform, so the swipe-down drag can move it.
  const [settled, setSettled] = useState(false);
  useScrollLock(true);

  // Opening from the Next up card: the card's photo grows into the hero as
  // the sheet rises. The sheet's own hero stays hidden until the copy has
  // landed and the real photo has loaded beneath it, so nothing pops.
  useEffect(() => {
    const h = takeHeroHandoff(id);
    const root = rootRef.current; const panel = panelRef.current;
    if (!h || !root || !panel || reduceMotion()) return undefined;
    const to = heroRestRect(panel);
    if (!to) return undefined;
    handoffRef.current = h;
    // A data attribute, not a class: React rewrites the panel's className
    // when it settles and would wipe a class added by hand.
    panel.dataset.handoff = '1';
    const g = makeGhost(root, h.src, h.rect, '16px', 'open');
    const anim = flyGhost(g, to, wide() ? '20px 20px 0 0' : '0px', 300);
    // The copy leaves once it has landed and the hero's own photo is on
    // screen (same file, so usually instant). Timers rather than animation
    // events alone: a tab that is not compositing never fires them.
    let done = false; let landed = false;
    const ready = () => !!panel.querySelector('.bd-hero-img.is-loaded');
    const finish = () => {
      if (done) return; done = true;
      clearInterval(poll); clearTimeout(cap); clearTimeout(land);
      delete panel.dataset.handoff;
      root.querySelectorAll('.bsheet-ghost[data-kind="open"]').forEach((n) => n.remove());
    };
    const poll = setInterval(() => { if (landed && ready()) finish(); }, 50);
    const land = setTimeout(() => { landed = true; }, 320);
    const cap = setTimeout(finish, 1800);
    anim.onfinish = () => { landed = true; if (ready()) finish(); };
    return finish;
  }, [id]);

  // Closing back to the card: the hero shrinks into the card's spot while the
  // sheet drops. The target is the rect measured at the tap: the list cannot
  // have scrolled under the lock, and measuring it now would catch it mid
  // way through easing back from its dimmed, scaled state.
  useEffect(() => {
    if (open || !handoffRef.current) return undefined;
    const root = rootRef.current; const panel = panelRef.current;
    const h = handoffRef.current;
    const card = document.querySelector(`.bl-hero[data-booking-id="${h.id}"] .bl-hero-img`);
    if (!root || !panel || !card || reduceMotion()) return undefined;
    const hero = panel.querySelector('.bd-hero');
    if (!hero) return undefined;
    const from = hero.getBoundingClientRect();
    panel.dataset.handoff = '1';
    const g = makeGhost(root, h.src, from, wide() ? '20px 20px 0 0' : '0px', 'close');
    const anim = flyGhost(g, h.rect, '16px 16px 0 0', CLOSE_MS);
    anim.onfinish = () => g.remove();
    return () => g.remove();
  }, [open]);

  // The rise is a CSS animation that starts the moment the sheet mounts, so
  // it is never held back by the work of rendering the page inside it. The
  // settle also runs on a timer: animationend does not fire while the view
  // is not compositing (a hidden tab), and the drag needs the settled state.
  useEffect(() => {
    hapticImpact();
    const t = setTimeout(() => setSettled(true), 320);
    return () => clearTimeout(t);
  }, []);

  // The list behind dims and eases back for exactly as long as the sheet is up.
  useEffect(() => {
    if (open) document.body.classList.add('bsheet-open');
    else { document.body.classList.remove('bsheet-open'); hapticTick(); }
    return () => document.body.classList.remove('bsheet-open');
  }, [open]);

  // Arrived by a tap from the list: go back to it. Arrived by a link: the
  // list is not in history, so replace instead of leaving the app.
  const close = useCallback(() => {
    if (history.action === 'PUSH') history.goBack();
    else history.replace('/dashboard/bookings');
  }, [history]);

  // Drag down from the top of the sheet to close. The sheet follows the
  // finger with a little resistance and the photo stretches; past the
  // threshold it closes, otherwise it springs back.
  useEffect(() => {
    const panel = panelRef.current;
    const scroller = scrollerRef.current;
    if (!panel || !scroller) return undefined;
    let startY = 0; let dy = 0; let dragging = false;
    const hero = () => panel.querySelector('.bd-hero');
    const onStart = (e) => {
      if (scroller.scrollTop > 0 || e.touches.length !== 1) return;
      startY = e.touches[0].clientY; dy = 0; dragging = true;
      panel.style.transition = 'none';
      if (backdropRef.current) backdropRef.current.style.transition = 'none';
    };
    const onMove = (e) => {
      if (!dragging) return;
      dy = Math.max(0, e.touches[0].clientY - startY);
      if (dy > 0 && scroller.scrollTop <= 0) {
        e.preventDefault();
        const eased = dy * 0.85;
        panel.style.transform = `translateY(${eased}px)`;
        if (backdropRef.current) backdropRef.current.style.opacity = String(Math.max(0, 1 - eased / 400));
        const h = hero(); if (h) h.style.setProperty('--bd-ps', String(1 + Math.min(dy, 240) / 900));
      }
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      panel.style.transition = ''; panel.style.transform = '';
      if (backdropRef.current) { backdropRef.current.style.transition = ''; backdropRef.current.style.opacity = ''; }
      const h = hero(); if (h) h.style.setProperty('--bd-ps', '1');
      if (dy > CLOSE_DRAG) close();
    };
    panel.addEventListener('touchstart', onStart, { passive: true });
    panel.addEventListener('touchmove', onMove, { passive: false });
    panel.addEventListener('touchend', onEnd);
    panel.addEventListener('touchcancel', onEnd);
    return () => {
      panel.removeEventListener('touchstart', onStart);
      panel.removeEventListener('touchmove', onMove);
      panel.removeEventListener('touchend', onEnd);
      panel.removeEventListener('touchcancel', onEnd);
    };
  }, [close]);

  // Escape closes, like the app's other sheets.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return createPortal(
    <div className={`bsheet-root${open ? ' is-open' : ''}`} ref={rootRef}>
      <div className="bsheet-backdrop" ref={backdropRef} onClick={close} aria-hidden="true" />
      <div className={`bsheet${settled ? ' is-settled' : ''}`} role="dialog" aria-modal="true" aria-label="Your booking" ref={panelRef} onAnimationEnd={(e) => { if (e.target === panelRef.current && open) setSettled(true); }}>
        <div className="bsheet-scroll" ref={scrollerRef}>
          <BookingDetailsPage bookingId={id} inSheet scrollerRef={scrollerRef} onClose={close} />
        </div>
      </div>
    </div>,
    document.body
  );
};

/* Renders the sheet for /dashboard/bookings/:id and keeps it mounted for the
   closing animation after the route has moved on. */
export const BookingSheetRoute = () => {
  const location = useLocation();
  const m = matchPath(location.pathname, { path: '/dashboard/bookings/:id', exact: true });
  const matchedId = m ? m.params.id : null;
  const [state, setState] = useState({ id: matchedId, open: !!matchedId });
  useEffect(() => {
    if (matchedId) { setState({ id: matchedId, open: true }); return undefined; }
    setState((s) => (s.id ? { ...s, open: false } : s));
    const t = setTimeout(() => setState({ id: null, open: false }), CLOSE_MS);
    return () => clearTimeout(t);
  }, [matchedId]);
  if (!state.id) return null;
  return <BookingSheet key={state.id} id={state.id} open={state.open} />;
};

export default BookingSheet;
